
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { VinResultsProps, NhtsaResponse } from "@/types/vehicle/vehicle-base";
import { UnmatchedFeaturesDisplay } from "./UnmatchedFeaturesDisplay";
import { findUnmatchedSafetyFeatures } from "@/utils/featureComparisonUtils";
import { useEffect, useState } from "react";
import { UnmatchedFeature } from "./UnmatchedFeaturesDisplay";
import { VehicleInfo } from "./VehicleInfo";
import { NHTSA_SAFETY_FIELDS } from "@/utils/feature-matching/constants";
import { AdasFeatures } from "../dataone/AdasFeatures";
import { EquipmentFeatures } from "../dataone/EquipmentFeatures";
import { VehicleOverview } from "../dataone/VehicleOverview";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ColorsMaterials } from "../dataone/ColorsMaterials";
import { VehicleImages } from "../dataone/VehicleImages";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DataOneUtils } from "../dataone/utils";

export function VinResults({ dataOneData, nhtsaData, onClose, isOpen }: VinResultsProps) {
  const [unmatchedFeatures, setUnmatchedFeatures] = useState<UnmatchedFeature[]>([]);
  const [activeTab, setActiveTab] = useState("summary");
  const [selectedStyleIndex, setSelectedStyleIndex] = useState<number>(0);
  const [availableStyles, setAvailableStyles] = useState<any[]>([]);
  
  // Extract available styles from DataOne data
  useEffect(() => {
    if (dataOneData) {
      try {
        const styles: any[] = [];
        const queryResponses = dataOneData.query_responses;
        
        if (queryResponses) {
          const firstResponseKey = Object.keys(queryResponses)[0];
          if (firstResponseKey) {
            const response = queryResponses[firstResponseKey];
            
            // Check different potential style locations in the data structure
            if (response?.styles?.style) {
              const styleData = response.styles.style;
              if (Array.isArray(styleData)) {
                styles.push(...styleData);
              } else {
                styles.push(styleData);
              }
            } else if (response?.us_market_data?.us_styles) {
              styles.push(...response.us_market_data.us_styles);
            }
          }
        }
        
        setAvailableStyles(styles);
        console.log("Available styles:", styles);
      } catch (error) {
        console.error("Error extracting styles:", error);
      }
    }
  }, [dataOneData]);
  
  // Generate feature comparison
  useEffect(() => {
    if (dataOneData) {
      // Check if we have cached comparison results from the database
      const cachedResults = (dataOneData as any).feature_comparison_results;
      
      if (cachedResults) {
        console.log("Using cached feature comparison results");
        setUnmatchedFeatures(cachedResults as UnmatchedFeature[]);
      } else if (nhtsaData) {
        console.log("Generating new feature comparison results");
        // This function would need to be updated to work with DataOne data instead of ChromeData
        const results = findUnmatchedSafetyFeatures(dataOneData, nhtsaData);
        setUnmatchedFeatures(results);
      }
    } else if (nhtsaData) {
      // Extract NHTSA safety features when DataOne is null
      const nhtsaFeatures: UnmatchedFeature[] = [];
      
      for (const field of NHTSA_SAFETY_FIELDS) {
        const value = nhtsaData[field as keyof typeof nhtsaData];
        if (value && value !== "Not Applicable" && value !== "N/A") {
          const normalizedValue = value.toLowerCase();
          let confidence: UnmatchedFeature["confidence"] = "Maybe Installed";
          
          if (normalizedValue.includes("standard")) {
            confidence = "Installed";
          } else if (normalizedValue.includes("optional")) {
            confidence = "Maybe Installed";
          } else if (normalizedValue.includes("no") || normalizedValue.includes("not available")) {
            confidence = "Not Installed";
          }

          // Make the feature name more readable
          const readableName = field
            .replace(/([A-Z])/g, ' $1')
            .replace(/_/g, ' ')
            .trim();

          nhtsaFeatures.push({
            name: readableName,
            value,
            confidence
          });
        }
      }
      setUnmatchedFeatures(nhtsaFeatures);
    }
  }, [dataOneData, nhtsaData]);

  if (!nhtsaData && !dataOneData) return null;

  const vin = dataOneData?.vehicle?.vin || nhtsaData?.VIN;

  // Construct complete NHTSA response object if needed
  const nhtsaResponse: NhtsaResponse | null = nhtsaData ? {
    Count: 1,
    Message: "Results returned successfully",
    Results: [nhtsaData]
  } : null;

  const handleCopyJson = () => {
    const jsonData = dataOneData || nhtsaResponse;
    if (jsonData) {
      navigator.clipboard.writeText(JSON.stringify(jsonData, null, 2));
      toast.success('JSON data copied to clipboard');
    }
  };
  
  // Get selected style data
  const currentStyleData = availableStyles.length > 0 ? availableStyles[selectedStyleIndex] : null;
  
  const styleSelectOptions = availableStyles.map((style, index) => {
    const styleName = style.name || style.style_id || style.trim || `Style ${index + 1}`;
    return { value: index.toString(), label: styleName };
  });

  // Create dataOneDataWithStyle to include selected style context
  const dataOneDataWithStyle = dataOneData ? {
    ...dataOneData,
    selectedStyle: currentStyleData,
    selectedStyleIndex: selectedStyleIndex
  } : null;
  
  // Get the style count for the info message
  const vehicleInfo = dataOneData ? DataOneUtils.getVehicleInfo(dataOneData) : null;
  const hasMultipleStyles = vehicleInfo?.hasMultipleStyles || availableStyles.length > 1;
  const styleCount = vehicleInfo?.styleCount || availableStyles.length;

  return (
    <Dialog open={isOpen} onOpenChange={() => onClose?.()}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold text-everest-teal">
            VIN Lookup Results: {vin}
          </DialogTitle>
        </DialogHeader>
        
        <Tabs defaultValue="summary" value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-4">
            <TabsTrigger value="summary">Summary View</TabsTrigger>
            <TabsTrigger value="raw">Raw JSON</TabsTrigger>
          </TabsList>
          
          <TabsContent value="summary">
            <ScrollArea className="h-[calc(100vh-250px)] w-full rounded-md border p-6">
              {/* Style selector for multiple styles - show only when there are multiple styles */}
              {hasMultipleStyles && availableStyles.length > 1 && (
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Select Style</h3>
                  <Select 
                    value={selectedStyleIndex.toString()} 
                    onValueChange={(value) => setSelectedStyleIndex(parseInt(value))}
                  >
                    <SelectTrigger className="w-full max-w-xs">
                      <SelectValue placeholder="Select style" />
                    </SelectTrigger>
                    <SelectContent>
                      {styleSelectOptions.map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">
                    This vehicle has {styleCount} different style options. Select a style to see specific details.
                  </p>
                </div>
              )}

              {/* Always display VehicleOverview first when DataOne data is available */}
              {dataOneData && (
                <div className="mb-8">
                  <VehicleOverview 
                    data={dataOneData} 
                    selectedStyle={currentStyleData} 
                    selectedStyleIndex={selectedStyleIndex}
                  />
                </div>
              )}

              {/* Maintain basic vehicle info display for NHTSA-only data */}
              {!dataOneData && nhtsaData && (
                <div className="mb-8">
                  <VehicleInfo 
                    data={nhtsaResponse} 
                    dataOneData={null}
                  />
                </div>
              )}

              {/* DataOne detailed sections */}
              {dataOneData && (
                <div className="space-y-8">
                  {/* ADAS Features Section */}
                  <div className="mb-8">
                    <h2 className="text-xl font-semibold text-everest-teal mb-4">ADAS Features</h2>
                    <AdasFeatures 
                      data={dataOneData} 
                      selectedStyle={currentStyleData} 
                      selectedStyleIndex={selectedStyleIndex}
                    />
                  </div>
                  
                  {/* Equipment Features Section */}
                  <div className="mb-8">
                    <h2 className="text-xl font-semibold text-everest-teal mb-4">Equipment Features</h2>
                    <EquipmentFeatures 
                      data={dataOneData} 
                      selectedStyle={currentStyleData} 
                      selectedStyleIndex={selectedStyleIndex}
                    />
                  </div>
                  
                  {/* Colors & Materials Section - Added */}
                  <div className="mb-8">
                    <h2 className="text-xl font-semibold text-everest-teal mb-4">Colors & Materials</h2>
                    <ColorsMaterials 
                      data={dataOneData} 
                      selectedStyle={currentStyleData} 
                      selectedStyleIndex={selectedStyleIndex}
                    />
                  </div>
                  
                  {/* Vehicle Images Section - Added */}
                  <div className="mb-8">
                    <h2 className="text-xl font-semibold text-everest-teal mb-4">Vehicle Images</h2>
                    <VehicleImages 
                      data={dataOneData} 
                      selectedStyle={currentStyleData} 
                      selectedStyleIndex={selectedStyleIndex}
                    />
                  </div>
                </div>
              )}
              
              {/* Features Section - show for NHTSA-only results */}
              {unmatchedFeatures.length > 0 && !dataOneData && (
                <div className="mt-8">
                  <UnmatchedFeaturesDisplay features={unmatchedFeatures} />
                </div>
              )}
            </ScrollArea>
          </TabsContent>
          
          <TabsContent value="raw">
            <div className="flex justify-end mb-4">
              <Button 
                size="sm" 
                variant="outline"
                onClick={handleCopyJson}
              >
                Copy JSON
              </Button>
            </div>
            <ScrollArea className="h-[calc(100vh-250px)] w-full rounded-md border">
              <Textarea
                readOnly
                value={JSON.stringify(dataOneData || nhtsaResponse, null, 2)}
                className="w-full text-xs font-mono bg-gray-50 border-none min-h-[300px] resize-none p-4"
                style={{
                  minWidth: 0,
                  width: "100%",
                  whiteSpace: "pre-wrap",
                }}
              />
            </ScrollArea>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
